import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  InputAdornment,
  IconButton,
  Alert,
} from '@mui/material';
import { Google, Apple, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState('');

  const onSubmit = async (data) => {
    try {
      const result = await login(data.email, data.password);
      if (result.success) {
        navigate('/onboarding');
      }
    } catch (err) {
      setError('Invalid credentials. Please try again.');
    }
  };

  const logoUrl = "https://horizons-cdn.hostinger.com/f34f6b4a-5701-45b6-9778-48a500b88e4d/00c7de63c68d9d1ea880547b4c0c04a6.png";

  const cleanField = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover fieldset': {
        borderColor: 'transparent',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'transparent',
        boxShadow: '0 0 0 2px rgba(0,0,0,0.15)',
      },
      transition: 'all 0.3s ease',
      background: 'rgba(255,255,255,0.85)',
    },
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
        animation: 'fadeIn 0.8s ease',
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ height: '80vh', display: 'flex', alignItems: 'center' }}
      >
        {/* FORM SECTION */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            animation: 'slideUp 0.7s ease',
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 4,
              width: '100%',
              maxWidth: 450,
              borderRadius: 3,
              background: 'rgba(255,255,255,0.65)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
            }}
          >
            {/* Logo + Tagline */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <img src={logoUrl} alt="RTNZERO Logo" style={{ width: 120, marginBottom: 12 }} />
              <Typography variant="h5" fontWeight="bold" color="black">
                RTNZERO
              </Typography>
              <Typography variant="body2" color="gray">
                Empowering your sustainable future
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* EMAIL */}
              <TextField
                fullWidth
                label="Email"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={cleanField}
              />

              {/* PASSWORD */}
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={cleanField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ textAlign: 'right', mb: 3 }}>
                <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary">
                    Forgot Password?
                  </Typography>
                </Link>
              </Box>

              {/* SIGN IN BUTTON */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mb: 3,
                  py: 1.5,
                  borderRadius: 2,
                  background: 'black',
                  color: 'white',
                  '&:hover': { background: '#333' },
                }}
              >
                Sign In
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="gray">
                  OR CONTINUE WITH
                </Typography>
              </Divider>

              {/* SOCIAL LOGIN */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Google />}
                    sx={{
                      borderColor: 'transparent',
                      color: 'black',
                      background: 'rgba(255,255,255,0.85)',
                      '&:hover': { background: 'rgba(0,0,0,0.05)' },
                      py: 1.3,
                      borderRadius: 2,
                    }}
                  >
                    Google
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Apple />}
                    sx={{
                      borderColor: 'transparent',
                      color: 'black',
                      background: 'rgba(255,255,255,0.85)',
                      '&:hover': { background: 'rgba(0,0,0,0.05)' },
                      py: 1.3,
                      borderRadius: 2,
                    }}
                  >
                    Apple
                  </Button>
                </Grid>
              </Grid>

              {/* SIGN UP LINK */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="gray">
                  Don't have an account?{' '}
                  <Link to="/signup" style={{ textDecoration: 'none' }}>
                    <Typography component="span" color="black" fontWeight="bold">
                      Sign Up
                    </Typography>
                  </Link>
                </Typography>
              </Box>
            </form>
          </Paper>
        </Grid>

        {/* OPTIONAL RIGHT IMAGE */}
        <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${logoUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: 3,
            }}
          />
        </Grid>
      </Grid>

      {/* KEYFRAME ANIMATIONS */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(25px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>
    </Container>
  );
};

export default Login;
